import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × TURNER: Light storms in code
// Gradient turbulence, motion blur, light particles
export function VasarelyTurnerMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Turbulent atmospheric background */}
      <defs>
        <radialGradient id="turner-storm" cx="0.4" cy="0.3">
          <stop offset="0%" stopColor={brand.amber} stopOpacity="0.6" />
          <stop offset="40%" stopColor={brand.aqua} stopOpacity="0.3" />
          <stop offset="70%" stopColor={brand.violet} stopOpacity="0.2" />
          <stop offset="100%" stopColor={brand.deep} stopOpacity="0.05" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="794" height="1123" fill="url(#turner-storm)" />

      <g transform="translate(397 540)">
        {/* Light particle storm */}
        {Array.from({ length: 300 }).map((_, i) => {
          const angle = (i / 300) * Math.PI * 4 + Math.random() * 0.5;
          const r = Math.random() * 350;
          const x = Math.cos(angle) * r;
          const y = Math.sin(angle) * r;
          const velocity = Math.random() * 20;
          
          return (
            <circle
              key={i}
              cx={x + velocity}
              cy={y}
              r={1 + Math.random() * 3}
              fill={Math.random() > 0.5 ? brand.amber : brand.aqua}
              opacity={0.4 + Math.random() * 0.4}
            />
          );
        })}

        {/* Turbulent wave patterns */}
        {Array.from({ length: 15 }).map((_, i) => {
          const y = -240 + i * 32;
          const amplitude = 60 + Math.sin(i * 0.5) * 30;
          const points = Array.from({ length: 40 }).map((_, j) => {
            const x = -300 + j * 15;
            const wave = Math.sin(j * 0.3 + i * 0.4) * amplitude;
            return `${x},${y + wave}`;
          }).join(" ");
          
          return (
            <polyline
              key={i}
              points={points}
              fill="none"
              stroke={i % 3 === 0 ? brand.amber : i % 3 === 1 ? brand.aqua : brand.violet}
              strokeWidth="2"
              opacity={0.3}
            />
          );
        })}

        {/* Motion blur streaks */}
        {Array.from({ length: 30 }).map((_, i) => {
          const angle = (i / 30) * Math.PI * 2;
          const r1 = 100 + Math.random() * 100;
          const r2 = r1 + 80;
          
          return (
            <line
              key={i}
              x1={Math.cos(angle) * r1}
              y1={Math.sin(angle) * r1}
              x2={Math.cos(angle) * r2}
              y2={Math.sin(angle) * r2}
              stroke={brand.amber}
              strokeWidth="3"
              opacity={0.15}
            />
          );
        })}

        {/* Swirling vortex structure */}
        {Array.from({ length: 40 }).map((_, i) => {
          const angle = i * 0.5;
          const r = 20 + i * 7;
          const distortion = Math.sin(i * 0.3) * 15;
          
          return (
            <ellipse
              key={i}
              cx={Math.cos(angle) * 10}
              cy={Math.sin(angle) * 10}
              rx={r + distortion}
              ry={r - distortion * 0.5}
              fill="none"
              stroke={i % 2 === 0 ? brand.aqua : brand.violet}
              strokeWidth="1.5"
              opacity={0.25 - i * 0.005}
              transform={`rotate(${angle * 20})`}
            />
          );
        })}

        {/* Atmospheric light sources */}
        {[
          { x: -100, y: -150, r: 60 },
          { x: 120, y: -100, r: 50 },
          { x: -80, y: 140, r: 55 },
        ].map((light, i) => (
          <g key={i}>
            <circle cx={light.x} cy={light.y} r={light.r} fill={brand.amber} opacity="0.2" />
            <circle cx={light.x} cy={light.y} r={light.r * 0.7} fill={brand.aqua} opacity="0.15" />
            <circle cx={light.x} cy={light.y} r={light.r * 0.4} fill={brand.white} opacity="0.3" />
          </g>
        ))}

        {/* Entropy grid (data turbulence) */}
        {Array.from({ length: 8 }).map((_, i) =>
          Array.from({ length: 8 }).map((_, j) => {
            const x = (i - 3.5) * 60;
            const y = (j - 3.5) * 60;
            const chaos = Math.sin(i * j * 0.5) * 20;
            
            return (
              <rect
                key={`${i}-${j}`}
                x={x - 15 + chaos}
                y={y - 15 - chaos}
                width="30"
                height="30"
                fill={brand.violet}
                opacity={0.1}
                transform={`rotate(${chaos * 2} ${x} ${y})`}
              />
            );
          })
        )}
      </g>

      {/* Storm inscription */}
      <text x="397" y="1050" textAnchor="middle" fontFamily="serif" fontSize="11" fill={brand.amber} opacity="0.6" fontStyle="italic">
        light and turbulence
      </text>
    </g>
  );
}
